import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from 'react';
import { useNavigate, Route, Routes, useLocation } from 'react-router-dom';
import { Button, Page, PageContentHeader, PageEmptyState, Typography, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import ErrorBoundaryPage from '~/components/ErrorBoundaryPage/ErrorBoundaryPage';
import LoadingIndicator from '~/components/LoadingIndicator';
import PageLoadingState from '~/components/PageLoadingState';
import { routes } from '~/constants';
import { useStore, useStoreActions } from '~/contexts';
import { useRelationship } from '~/hooks/reactQuery/queries/useRelationship';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOnMount } from '~/hooks/useOnMount';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { relationshipModel } from '~/models';
import ActivityLog from '~/pages/AgreementMinisite/ActivityLog/ActivityLog';
import { buildMinisiteTabs } from '~/pages/AgreementMinisite/AgreementMinisite';
import AgreementMinisiteTopBar from '~/pages/AgreementMinisite/AgreementMinisiteTopBar/AgreementMinisiteTopBar';
import { AgreementView } from '~/pages/AgreementMinisite/AgreementView/AgreementView';
import BillingHub from '~/pages/AgreementMinisite/BillingHub/BillingHub';
import InvoicesView from '~/pages/AgreementMinisite/InvoicesView/InvoicesView';
import PaymentMethods from '~/pages/AgreementMinisite/PaymentMethods/PaymentMethods';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import SeeWhatItsLike from '../../components/SeeWhatItsLike';
import { BillingHubUrlParams } from '../AgreementMinisite/BillingHub/BillingHub.utils';
import { AdHocAgreementEmptyState } from './AdHocAgreementEmptyState/AdHocAgreementEmptyState';
import { BulkBillingModal } from './BulkBillingModal/BulkBillingModal';
import { GBHMapEntitiesCallout } from './GBHMapEntitiesCallout/GBHMapEntitiesCallout';
import styles from './GlobalBillingHub.module.scss';
import { buildSeeWhatItsLikeSteps, getBillableClientIds, resolveBillingHubEntryNavigation } from './GlobalBillingHub.utils';
import { GlobalBillingHubData } from './GlobalBillingHubData';
import { GlobalBillingHubDataOld } from './GlobalBillingHubDataOld';
import { GlobalBillingHubHeader } from './GlobalBillingHubHeader/GlobalBillingHubHeader';
import { useFetchRelationshipSummaries } from './hooks/useFetchRelationshipSummaries';
import GlobalBillingHubEmptyStateIcon from './img/GlobalBillingHubEmptyStateIcon';
export function BillingHubRelationshipFetcher(param) {
    let { relationshipId } = param;
    const isVendor = useStore(selectors.user.isVendor);
    const { relationship, isLoading, error, refresh: fetchRelationship } = useRelationship(relationshipId);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const isMobileDevice = useIsMobileForHomePage();
    const showMappingCallouts = !isMobileDevice;
    useOnMount(()=>{
        fetchRelationship({
            silent: true
        });
    });
    if (error) throw makeSentryIgnoredError('Error fetching relationship');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isLoading && /*#__PURE__*/ _jsx(LoadingIndicator, {}),
            relationship && /*#__PURE__*/ _jsxs("div", {
                children: [
                    showMappingCallouts && /*#__PURE__*/ _jsx("div", {
                        className: styles.calloutContainer,
                        children: /*#__PURE__*/ _jsx(PageContentHeader, {
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: styles.callouts,
                                children: [
                                    showQboIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.QBO
                                    }),
                                    showXeroIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                        services: relationship.services,
                                        clientID: relationship.clientContactId,
                                        refreshAgreement: fetchRelationship,
                                        accounting: Accounting.Xero
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(BillingHub, {
                        isVendor: isVendor,
                        agreement: relationship,
                        refreshAgreement: fetchRelationship
                    })
                ]
            })
        ]
    });
}
export function GlobalBillingHubTabbedView(param) {
    let { relationshipId } = param;
    const isVendor = useStore(selectors.user.isVendor);
    const { relationship, isLoading, error, refresh } = useRelationship(relationshipId);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = routes.globalBillingHub;
    const { t: agreementMinisiteTexts } = useLocaleKeys('pages.agreementMinisite');
    const minisiteTabs = buildMinisiteTabs(agreementMinisiteTexts.tabs);
    const isMobileDevice = useIsMobileForHomePage();
    const showMappingCallouts = !isMobileDevice;
    useOnMount(()=>{
        refresh({
            silent: true
        });
    });
    useOnMount(()=>{
        // This subtree is keyed by relationshipId, so it remounts on every client/agreement switch — drop the
        // previous relationship's Activity Log search/filter params so they don't leak into the new client, and
        // land on a valid tab. (ANCR-3172 / ANCR-3226)
        const target = resolveBillingHubEntryNavigation({
            pathname: location.pathname,
            search: location.search,
            basePath,
            knownTabUrls: Object.values(minisiteTabs).map((t)=>t.url),
            defaultTabUrl: minisiteTabs.billingHub.url
        });
        if (target) navigate(target, {
            replace: true
        });
    });
    if (error) throw makeSentryIgnoredError('Error fetching relationship');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isLoading && /*#__PURE__*/ _jsx(LoadingIndicator, {}),
            relationship && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.calloutContainer,
                        children: /*#__PURE__*/ _jsxs(PageContentHeader, {
                            children: [
                                showMappingCallouts && /*#__PURE__*/ _jsxs("div", {
                                    className: styles.callouts,
                                    children: [
                                        showQboIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                            services: relationship.services,
                                            clientID: relationship.clientContactId,
                                            refreshAgreement: refresh,
                                            accounting: Accounting.QBO
                                        }),
                                        showXeroIntegration && /*#__PURE__*/ _jsx(GBHMapEntitiesCallout, {
                                            services: relationship.services,
                                            clientID: relationship.clientContactId,
                                            refreshAgreement: refresh,
                                            accounting: Accounting.Xero
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.tabBarWrapper,
                                    children: /*#__PURE__*/ _jsx(AgreementMinisiteTopBar, {
                                        relationship: relationship,
                                        tabs: minisiteTabs
                                    })
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(ErrorBoundaryPage, {
                        bounded: true,
                        children: /*#__PURE__*/ _jsxs(Routes, {
                            children: [
                                /*#__PURE__*/ _jsx(Route, {
                                    path: minisiteTabs.billingHub.url,
                                    element: /*#__PURE__*/ _jsx(BillingHub, {
                                        isVendor: isVendor,
                                        agreement: relationship,
                                        refreshAgreement: refresh
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: "".concat(minisiteTabs.agreement.url, "/*"),
                                    element: /*#__PURE__*/ _jsx("div", {
                                        className: styles.tabContent,
                                        children: relationshipModel.isAdHoc(relationship) ? /*#__PURE__*/ _jsx(AdHocAgreementEmptyState, {
                                            clientContactId: relationship.clientContactId
                                        }) : /*#__PURE__*/ _jsx(AgreementView, {
                                            relationship: relationship,
                                            isVendor: isVendor,
                                            refresh: refresh
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: minisiteTabs.paymentMethods.url,
                                    element: /*#__PURE__*/ _jsx(PaymentMethods, {
                                        isVendor: isVendor,
                                        agreement: relationship,
                                        onRefreshAgreement: refresh
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: "".concat(minisiteTabs.invoices.url, "/*"),
                                    element: /*#__PURE__*/ _jsx(InvoicesView, {
                                        agreement: relationship,
                                        viewMode: isVendor ? 'vendor' : 'customer',
                                        refreshAgreement: refresh
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Route, {
                                    path: "".concat(minisiteTabs.activity.url, "/*"),
                                    element: /*#__PURE__*/ _jsx(ActivityLog, {
                                        agreement: relationship
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
}
export function GlobalBillingHubWithSummaries(param) {
    let { agreements, isBackgroundLoading, BH } = param;
    const { t: gbhTexts } = useLocaleKeys('pages.globalBillingHub');
    const { AD_HOC_INVOICING: isAdHocInvoicingEnabled, FE_CRM } = useFeatureFlags();
    const { urlState: { clientId, relationshipId }, changeUrlState } = useStateFromUrl({
        [BillingHubUrlParams.ClientId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.RelationshipId]: {
            defaultValue: ''
        },
        [BillingHubUrlParams.SelectedDate]: {
            defaultValue: ''
        }
    });
    const scrollElRef = useRef(null);
    const { showModal } = useOverlayContext();
    const { logBI } = useStoreActions();
    const handleBulkBilling = ()=>{
        logBI(18700);
        void showModal({
            biName: 'bulk_billing_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BulkBillingModal, {
                    dismissModal: dismissModal
                });
            }
        });
    };
    const DataProvider = FE_CRM ? GlobalBillingHubData : GlobalBillingHubDataOld;
    return /*#__PURE__*/ _jsx(DataProvider, {
        agreements: agreements,
        clientId: clientId,
        relationshipId: relationshipId,
        changeUrlState: changeUrlState,
        children: (param)=>{
            let { clientOptions, groupedOptions, selectedClient } = param;
            return /*#__PURE__*/ _jsx(Page, {
                header: /*#__PURE__*/ _jsx(GlobalBillingHubHeader, {
                    changeUrlState: changeUrlState,
                    agreements: agreements,
                    clientOptions: clientOptions,
                    selectedClient: selectedClient,
                    groupedOptions: groupedOptions,
                    relationshipId: relationshipId
                }),
                loading: isBackgroundLoading,
                ref: scrollElRef,
                actions: !clientId && /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'open_bulk_billing'
                    },
                    skin: "primaryOutline",
                    intent: Intent.Bill,
                    testId: GlobalBillingHubTestIds.BulkBillingButton(),
                    onClick: handleBulkBilling,
                    children: gbhTexts.billClientsInBulk()
                }),
                children: relationshipId ? BH || (isAdHocInvoicingEnabled ? /*#__PURE__*/ _jsx(GlobalBillingHubTabbedView, {
                    relationshipId: relationshipId
                }, relationshipId) : /*#__PURE__*/ _jsx(BillingHubRelationshipFetcher, {
                    relationshipId: relationshipId
                }, relationshipId)) : /*#__PURE__*/ _jsx(Typography, {
                    testId: GlobalBillingHubTestIds.NoAgreement(),
                    className: styles.unselected,
                    variant: "paragraph1",
                    children: gbhTexts.pleaseChooseClient()
                })
            });
        }
    });
}
export const GetGlobalBillingHubTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            GlobalBillingHubTestIds: {
                BulkBillingButton: 'GlobalBillingHubTestIds.BulkBillingButton',
                NoAgreement: 'gbh-no-agreement',
                Empty: 'gbh-empty'
            }
        }
    });
export const { GlobalBillingHubTestIds } = GetGlobalBillingHubTID();
export default function GlobalBillingHub() {
    const { t: globalBillingHubTexts } = useLocaleKeys('pages.globalBillingHub');
    const location = useLocation();
    useSetDocumentTitle('Billing');
    const { summaries, isLoading, isBackgroundLoading, error } = useFetchRelationshipSummaries({
        refreshOnHookMount: true
    });
    const pinnedClientId = new URLSearchParams(location.search).get(BillingHubUrlParams.ClientId);
    // WHY: rendering while the pinned client is missing from stale summaries would wipe the URL pin (ANCR-2417) — hold only until the pin resolves
    const holdForUnresolvedPinnedClient = !!pinnedClientId && isBackgroundLoading && !getBillableClientIds(summaries).has(pinnedClientId);
    if (error) throw makeSentryIgnoredError('Error fetching relationship summaries');
    if (isLoading || holdForUnresolvedPinnedClient) return /*#__PURE__*/ _jsx(PageLoadingState, {});
    if (!summaries.length) {
        return /*#__PURE__*/ _jsx(Page, {
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                testId: GlobalBillingHubTestIds.Empty(),
                illustration: /*#__PURE__*/ _jsx(GlobalBillingHubEmptyStateIcon, {}),
                title: globalBillingHubTexts.emptyTitle(),
                message: globalBillingHubTexts.emptyMessage(),
                actions: /*#__PURE__*/ _jsx(SeeWhatItsLike, {
                    steps: buildSeeWhatItsLikeSteps(globalBillingHubTexts)
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(GlobalBillingHubWithSummaries, {
        agreements: summaries,
        isBackgroundLoading: isBackgroundLoading
    });
}
